import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, RefreshCw, Inbox, Star, Settings, X, Trash2 } from 'lucide-react'
import { emailAPI } from '../../services/api'
import { useModalClose } from '../../hooks/useModalClose'
import toast from 'react-hot-toast'
import DOMPurify from 'dompurify'

interface EmailWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Email {
  id: number
  subject: string
  sender_name: string
  sender_email: string
  body_preview: string
  body_html?: string
  is_read: boolean
  is_starred: boolean
  received_at: string
  provider: string
  category?: string
}

interface EmailConnection {
  id: number
  provider: string
  provider_email: string
  is_active: boolean
  last_sync_at: string | null
}

const CATEGORIES = [
  { id: 'primary', label: 'Primary', icon: '📥' },
  { id: 'social', label: 'Social', icon: '👥' },
  { id: 'promotions', label: 'Promotions', icon: '🏷️' },
  { id: 'updates', label: 'Updates', icon: '🔔' },
  { id: 'forums', label: 'Forums', icon: '💬' },
]

export default function EmailWidget({ widget, isExpanded }: EmailWidgetProps) {
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState('primary')
  const [selectedEmails, setSelectedEmails] = useState<number[]>([])
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null)
  const [isMouseOverDelete, setIsMouseOverDelete] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [condensedView, setCondensedView] = useState(false)
  const queryClient = useQueryClient()

  // Modal close handler
  useModalClose(() => {
    setShowSettings(false)
    setSelectedEmail(null)
    setSelectedEmails([])
    setLastSelectedIndex(null)
  })

  // Fetch email connections
  const { data: connections = [], isLoading: connectionsLoading, refetch: refetchConnections } = useQuery({
    queryKey: ['email-connections'],
    queryFn: async () => {
      const { data } = await emailAPI.getConnections()
      return data as EmailConnection[]
    },
  })

  // Fetch emails
  const { data: emails = [], isLoading: emailsLoading, refetch: refetchEmails } = useQuery({
    queryKey: ['emails'],
    queryFn: async () => {
      const { data } = await emailAPI.getAll({ limit: 50 })
      return data as Email[]
    },
    enabled: connections.length > 0,
  })

  const hasConnections = connections.length > 0

  // Auto-refresh emails every 2 minutes, but skip if mouse is over delete buttons
  useEffect(() => {
    if (!hasConnections) return

    const interval = setInterval(() => {
      if (!isMouseOverDelete && !isRefreshing) {
        console.log('Auto-refreshing emails...')
        refetchEmails()
      } else if (isMouseOverDelete) {
        console.log('Skipping auto-refresh - mouse over delete button')
      }
    }, 2 * 60 * 1000) // 2 minutes

    return () => clearInterval(interval)
  }, [hasConnections, isMouseOverDelete, isRefreshing, refetchEmails])

  // Filter emails by category
  const filteredEmails = emails.filter(e => (e.category || 'primary') === selectedCategory)
  const unreadCount = filteredEmails.filter(e => !e.is_read).length

  // Count emails per category
  const categoryCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = emails.filter(e => (e.category || 'primary') === cat.id).length
    return acc
  }, {} as Record<string, number>)

  const handleConnectGmail = async () => {
    try {
      const { data } = await emailAPI.getGmailAuthUrl()
      if (data.auth_url) {
        const width = 600
        const height = 700
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2
        
        window.open(
          data.auth_url,
          'Gmail OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        )
        
        const checkInterval = setInterval(() => {
          if (window.location.search.includes('email_sync=success')) {
            clearInterval(checkInterval)
            toast.success('Gmail connected successfully!')
            refetchConnections()
            handleSync('gmail')
          }
        }, 1000)
        
        setTimeout(() => clearInterval(checkInterval), 60000)
      } else if (data.error) {
        toast.error(data.error)
      }
    } catch (error: any) {
      console.error('Gmail OAuth error:', error)
      toast.error(error.response?.data?.error || 'Failed to connect Gmail')
    }
  }

  const handleConnectOutlook = async () => {
    try {
      const { data } = await emailAPI.getOutlookAuthUrl()
      if (data.auth_url) {
        const width = 600
        const height = 700
        const left = window.screen.width / 2 - width / 2
        const top = window.screen.height / 2 - height / 2
        
        window.open(
          data.auth_url,
          'Outlook OAuth',
          `width=${width},height=${height},left=${left},top=${top}`
        )
        
        const checkInterval = setInterval(() => {
          if (window.location.search.includes('email_sync=success')) {
            clearInterval(checkInterval)
            toast.success('Outlook connected successfully!')
            refetchConnections()
            handleSync('outlook')
          }
        }, 1000)
        
        setTimeout(() => clearInterval(checkInterval), 60000)
      } else if (data.error) {
        toast.error(data.error)
      }
    } catch (error: any) {
      console.error('Outlook OAuth error:', error)
      toast.error(error.response?.data?.error || 'Failed to connect Outlook')
    }
  }

  const handleSync = async (provider: string) => {
    try {
      await emailAPI.sync(provider)
      // Refresh emails in background without showing loading state
      setTimeout(() => refetchEmails(), 2000)
    } catch (error: any) {
      toast.error(error.response?.data?.error || `Failed to sync ${provider}`)
    }
  }

  const handleRefresh = async () => {
    if (isRefreshing) return

    setIsRefreshing(true)
    try {
      await refetchEmails()
    } catch (error) {
      console.error('Failed to refresh emails:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDisconnect = async (provider: string) => {
    if (!confirm(`Disconnect ${provider}? This will remove all synced emails.`)) return
    
    try {
      await emailAPI.disconnect(provider)
      toast.success(`${provider} disconnected`)
      refetchConnections()
      refetchEmails()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to disconnect')
    }
  }

  const handleMarkRead = async (email: Email) => {
    try {
      await emailAPI.markRead(email.id, true)
      queryClient.setQueryData(['emails'], (old: Email[] = []) =>
        old.map(e => e.id === email.id ? { ...e, is_read: true } : e)
      )
    } catch (error) {
      console.error('Failed to mark as read:', error)
    }
  }

  const handleStar = async (email: Email) => {
    try {
      await emailAPI.star(email.id, !email.is_starred)
      queryClient.setQueryData(['emails'], (old: Email[] = []) =>
        old.map(e => e.id === email.id ? { ...e, is_starred: !e.is_starred } : e)
      )
    } catch (error) {
      console.error('Failed to star:', error)
    }
  }

  const handleDelete = async (email: Email) => {
    try {
      await emailAPI.delete(email.id)
      queryClient.setQueryData(['emails'], (old: Email[] = []) =>
        old.filter(e => e.id !== email.id)
      )
      if (selectedEmail?.id === email.id) {
        setSelectedEmail(null)
      }
      toast.success('Email deleted successfully')
    } catch (error) {
      console.error('Failed to delete email:', error)
      toast.error('Failed to delete email')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedEmails.length === 0) return

    try {
      await Promise.all(selectedEmails.map(id => emailAPI.delete(id)))
      queryClient.setQueryData(['emails'], (old: Email[] = []) =>
        old.filter(e => !selectedEmails.includes(e.id))
      )
      setSelectedEmails([])
      setLastSelectedIndex(null)
      if (selectedEmail && selectedEmails.includes(selectedEmail.id)) {
        setSelectedEmail(null)
      }
      toast.success(`${selectedEmails.length} emails deleted successfully`)
    } catch (error) {
      console.error('Failed to delete emails:', error)
      toast.error('Failed to delete some emails')
    }
  }

  const handleEmailSelection = (email: Email, index: number, isChecked: boolean, isShiftClick: boolean = false) => {
    if (isShiftClick && lastSelectedIndex !== null) {
      // Shift-click: select range
      const startIndex = Math.min(lastSelectedIndex, index)
      const endIndex = Math.max(lastSelectedIndex, index)
      const emailsToSelect = filteredEmails.slice(startIndex, endIndex + 1).map(e => e.id)

      if (isChecked) {
        // Add the range to selection
        setSelectedEmails(prev => [...new Set([...prev, ...emailsToSelect])])
      } else {
        // Remove the range from selection
        setSelectedEmails(prev => prev.filter(id => !emailsToSelect.includes(id)))
      }
    } else {
      // Regular click
      if (isChecked) {
        setSelectedEmails(prev => [...prev, email.id])
      } else {
        setSelectedEmails(prev => prev.filter(id => id !== email.id))
      }
      setLastSelectedIndex(index)
    }
  }

  // Render Email Modal
  if (selectedEmail) {
    return createPortal(
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={() => setSelectedEmail(null)}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {selectedEmail.subject || '(No Subject)'}
            </h2>
            <button
              onClick={() => setSelectedEmail(null)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Email Details */}
          <div className="px-4 py-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="font-semibold text-gray-900 dark:text-white">
                  {selectedEmail.sender_name || selectedEmail.sender_email}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedEmail.sender_email}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleStar(selectedEmail)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  <Star
                    size={18}
                    className={selectedEmail.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}
                  />
                </button>
                <button
                  onClick={() => handleDelete(selectedEmail)}
                  onMouseEnter={() => setIsMouseOverDelete(true)}
                  onMouseLeave={() => setIsMouseOverDelete(false)}
                  className="p-2 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                  title="Delete email"
                >
                  <Trash2 size={18} className="text-red-500" />
                </button>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(selectedEmail.received_at).toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Email Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedEmail.body_html ? (
              <div
                className="email-content prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(selectedEmail.body_html, {
                    ADD_TAGS: ['style'],
                    ADD_ATTR: ['target'],
                  }),
                }}
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-800 dark:text-gray-200">
                {selectedEmail.body_preview}
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header with Actions */}
      <div className="flex items-center justify-between p-3 border-b dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Mail size={20} className="text-gray-700 dark:text-gray-300" />
          <span className="font-semibold text-gray-900 dark:text-white">Email</span>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 text-xs text-white rounded-full" style={{ backgroundColor: 'var(--color-primary)' }}>
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedEmails.length > 0 && (
            <button
              onClick={handleBulkDelete}
              onMouseEnter={() => setIsMouseOverDelete(true)}
              onMouseLeave={() => setIsMouseOverDelete(false)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 transition-colors"
              style={{ backgroundColor: '#EF4444' }}
              title={`Delete ${selectedEmails.length} selected emails`}
            >
              <Trash2 size={16} />
              Delete ({selectedEmails.length})
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={!hasConnections || isRefreshing}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={18} className={`text-gray-700 dark:text-gray-300 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Settings"
          >
            <Settings size={18} className="text-gray-700 dark:text-gray-300" />
          </button>
        </div>
      </div>

      {/* Category Tabs */}
      {hasConnections && (
        <div className="flex border-b dark:border-gray-700 overflow-x-auto">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                selectedCategory === cat.id
                  ? 'border-b-2 text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              style={selectedCategory === cat.id ? { borderColor: 'var(--color-primary)' } : {}}
            >
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
              {categoryCounts[cat.id] > 0 && (
                <span className="text-xs text-gray-500 dark:text-gray-400">({categoryCounts[cat.id]})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Email List */}
      <div className="flex-1 overflow-y-auto">
        {!hasConnections ? (
          <div className="flex flex-col items-center justify-center h-full p-6 text-center">
            <Mail size={48} className="mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Connect Your Email
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Connect Gmail or Outlook to view your emails
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleConnectGmail}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition flex items-center gap-2"
                style={{ backgroundColor: '#EA4335' }}
              >
                <Mail size={18} />
                Connect Gmail
              </button>
              <button
                onClick={handleConnectOutlook}
                className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition flex items-center gap-2"
                style={{ backgroundColor: '#0078D4' }}
              >
                <Mail size={18} />
                Connect Outlook
              </button>
            </div>
          </div>
        ) : emailsLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white mb-2"></div>
            <p>Loading {CATEGORIES.find(c => c.id === selectedCategory)?.label} emails...</p>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 p-4">
            <Inbox size={48} className="mb-2" />
            <p>No emails in {CATEGORIES.find(c => c.id === selectedCategory)?.label}</p>
          </div>
        ) : (
          filteredEmails.map((email, index) => {
            const isSelected = selectedEmails.includes(email.id)
            return (
              <div
                key={email.id}
                className={`group p-3 border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                  !email.is_read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                } ${isSelected ? 'bg-blue-100 dark:bg-blue-900/30' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      const isShiftClick = e.nativeEvent.shiftKey
                      handleEmailSelection(email, index, e.target.checked, isShiftClick)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div
                    className="flex-1"
                    onClick={() => {
                      setSelectedEmail(email)
                      if (!email.is_read) handleMarkRead(email)
                    }}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className={`flex-1 text-gray-900 dark:text-white ${!email.is_read ? 'font-semibold' : ''}`}>
                        {email.sender_name || email.sender_email}
                      </div>
                      <div className="flex items-center gap-2">
                        {email.is_starred && (
                          <Star size={14} className="fill-yellow-400 text-yellow-400" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(email)
                          }}
                          onMouseEnter={() => setIsMouseOverDelete(true)}
                          onMouseLeave={() => setIsMouseOverDelete(false)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors opacity-0 group-hover:opacity-100"
                          title="Delete email"
                        >
                          <Trash2 size={14} className="text-red-500" />
                        </button>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(email.received_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className={`text-sm mb-1 text-gray-900 dark:text-white ${!email.is_read ? 'font-medium' : ''}`}>
                      {email.subject || '(No Subject)'}
                    </div>
                    {!condensedView && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {email.body_preview}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowSettings(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Email Settings</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-gray-900 dark:text-white">Connected Accounts</h4>
              
              {connections.length === 0 ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">No accounts connected</p>
              ) : (
                connections.map(conn => (
                  <div key={conn.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white capitalize">{conn.provider}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{conn.provider_email}</div>
                    </div>
                    <button
                      onClick={() => handleDisconnect(conn.provider)}
                      className="px-3 py-1 text-sm text-white rounded-lg hover:opacity-90 transition"
                      style={{ backgroundColor: '#EF4444' }}
                    >
                      Disconnect
                    </button>
                  </div>
                ))
              )}

              <div className="pt-4 border-t dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Add Account</h4>
                <div className="space-y-2">
                  <button
                    onClick={handleConnectGmail}
                    disabled={connections.some(c => c.provider === 'gmail')}
                    className="w-full text-white px-4 py-3 rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#EA4335' }}
                  >
                    <Mail size={18} />
                    {connections.some(c => c.provider === 'gmail') ? 'Gmail Connected' : 'Connect Gmail'}
                  </button>
                  <button
                    onClick={handleConnectOutlook}
                    disabled={connections.some(c => c.provider === 'outlook')}
                    className="w-full text-white px-4 py-3 rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#0078D4' }}
                  >
                    <Mail size={18} />
                    {connections.some(c => c.provider === 'outlook') ? 'Outlook Connected' : 'Connect Outlook'}
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Display Options</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Condensed View</label>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Show only sender, subject, and date</p>
                    </div>
                    <button
                      onClick={() => setCondensedView(!condensedView)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors border-2 ${
                        condensedView
                          ? 'bg-blue-600 border-blue-600'
                          : 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          condensedView ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}











